import React, { FC, ReactNode } from 'react';

require('./OWSubTabs.less');

export interface OWSubTab {
	key: string;
	label: ReactNode;
	// Rendered greyed out and unclickable — for tabs whose surface is not available yet.
	disabled?: boolean;
}

interface OWSubTabsProps {
	tabs: OWSubTab[];
	activeKey: string;
	onChange: (key: string) => void;
	className?: string;
	// Set when the strip shares a header row with a search box or other controls: it holds its content
	// width and hangs the labels off the row's bottom edge so the baseline lines up with them.
	inline?: boolean;
}

// Underline-style secondary tab row (design system "Tabs/Top"): a subtle
// full-width baseline under the strip, with the active tab marked by a themed
// underline (primary color — purple for buyer, teal for supplier).
//
// Built as buttons with a plain CSS border rather than antd Tabs on purpose:
// antd's active-tab ink bar is positioned by JS-measuring the active tab and
// mis-renders (0 width) when mounted inside a route-driven flex header. A CSS
// border-bottom always paints.
export const OWSubTabs: FC<OWSubTabsProps> = ({
	tabs,
	activeKey,
	onChange,
	className = '',
	inline,
}): React.ReactElement => {
	const renderTab = (tab: OWSubTab) => {
		const handleClick = () => {
			if (tab.disabled) {
				return;
			}
			onChange(tab.key);
		};
		const isActive = tab.key === activeKey;
		// Weight carries the selection alongside the underline: H5/Regular at rest,
		// H5/Medium once selected.
		const typographyClassName = isActive ? 'h5medium' : 'h5regular';
		return (
			<div
				key={tab.key}
				role="button"
				aria-disabled={!!tab.disabled}
				onClick={handleClick}
				className={`ow-sub-tab ${typographyClassName} ${isActive ? 'ow-sub-tab--active' : ''} ${
					tab.disabled ? 'ow-sub-tab--disabled' : ''
				}`}
			>
				{tab.label}
			</div>
		);
	};

	return (
		<div
			className={`ow-sub-tabs flex flex-row items-center gap-8 border-0 border-b border-solid border-grey-5 ${
				inline ? 'ow-sub-tabs--inline' : ''
			} ${className}`}
		>
			{tabs.map(renderTab)}
		</div>
	);
};
